import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import MedicalCertificateIcon from '../../assets/images/icons/prescription/medicalcertificate.svg';
import ExamIcon from '../../assets/images/icons/prescription/exam.svg';
import MedicineIcon from '../../assets/images/icons/prescription/medicine.svg';
import FormulaIcon from '../../assets/images/icons/prescription/formula.svg';
import MedicalReferralIcon from '../../assets/images/icons/prescription/medicalreferral.svg';
import MedicalReportIcon from '../../assets/images/icons/prescription/medicalreport.svg';

import { HorizontalScrollContainer } from './styles';
import { GridItem } from '../UI-CHAKRA/GridItem';
import { PrescriptionContext, PrescriptionContextProps } from '../../providers/PrescriptionProvider';
import { Skeleton } from '../UI-CHAKRA/Skeleton';
import { SEARCH_TYPE, SEARCH_TYPE_MAP } from './SearchTypeEnum';
import { Anchor } from '../notifications/Anchor';
import { ANCHORS, ANCHOR_POS } from '../notifications/notifications';
import { Box } from '../UI-CHAKRA/Box';
import { usePrescriptionStore } from '../../stores/prescriptionStore';

export interface ItemContent {
  type: SEARCH_TYPE;
  label: string;
  icon: string;
  searchable: boolean;
  visible: boolean;
  itemCount?: number;
}

interface OptionButtonProps {
  buttonData: ItemContent;
  onClick(): void;
  isSelected: boolean;
  primaryColor: string;
}

const OptionButton: FunctionComponent<OptionButtonProps> = ({ buttonData, onClick, isSelected, primaryColor }) => {
  const activeColor = primaryColor || '#3D0E6B';

  return (
    <GridItem
      className={`option-grid ${isSelected && 'active'}`}
      onClick={onClick}
      data-cy={`option-grid-${buttonData.type}`}
    >
      <Anchor anchorName={ANCHORS[SEARCH_TYPE_MAP[buttonData.type]]} position={ANCHOR_POS.BOTTOM}>
        <button
          data-testid={buttonData.type}
          style={{
            background: 'transparent',
            border: 'none',
            borderBottom: isSelected ? `2px solid ${activeColor}` : '2px solid transparent',
            padding: '10px 8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: isSelected ? 700 : 500,
            color: isSelected ? activeColor : '#495057',
            whiteSpace: 'nowrap',
            transition: 'all 0.15s',
            lineHeight: '20px',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          {buttonData.label}
          {buttonData.itemCount !== undefined && buttonData.itemCount > 0 && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: activeColor,
                color: '#fff',
                fontSize: '11px',
                fontWeight: 700,
                lineHeight: 1,
                padding: '0 4px',
              }}
            >
              {buttonData.itemCount}
            </span>
          )}
        </button>
      </Anchor>
    </GridItem>
  );
};

interface SearchTypesProps {
  onChange(item: ItemContent): void;
  canAddExam: boolean;
  canAddMedicalCertificate: boolean;
  canAddMedicalReferral: boolean;
  selectedSearchType: SEARCH_TYPE;
  primaryColor: string;
  canAddMedicalReport: boolean;
  canShowMedicalPrescription: boolean;
  canShowFormula: boolean;
  canShowPrescription?: boolean;
}

function SearchTypes(props: SearchTypesProps) {
  const { prescriptionLoading } = useContext<PrescriptionContextProps>(PrescriptionContext);

  const {
    canAddExam,
    canAddMedicalCertificate,
    selectedSearchType,
    canAddMedicalReferral,
    onChange,
    canAddMedicalReport,
    canShowMedicalPrescription,
    canShowFormula,
    canShowPrescription,
  } = props;

  const [showBlurRight, setShowBlurRight] = useState(true);
  const [showBlurLeft, setShowBlurLeft] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const prescriberConfigurations = usePrescriptionStore((store) => store.prescriberConfiguration);
  const medicineCount = usePrescriptionStore((store) => {
    const medicines = store.prescription?.Itens || [];
    const manualMedicines = store.prescription?.ItensManuais || [];
    return medicines.length + manualMedicines.length;
  });
  const formulaCount = usePrescriptionStore((store) => store.prescription?.Formulas?.length || 0);
  const examCount = usePrescriptionStore((store) => store.prescription?.Exames?.length || 0);
  const certificateCount = usePrescriptionStore((store) => store.prescription?.Atestados?.length || 0);
  const referralCount = usePrescriptionStore((store) => store.prescription?.Encaminhamentos?.length || 0);
  const reportCount = usePrescriptionStore((store) => store.prescription?.Relatorios?.length || 0);

  const searchTypeContent = useMemo(() => {
    return [
      {
        type: SEARCH_TYPE.MEDICINE,
        label: canShowMedicalPrescription ? SEARCH_TYPE.MEDICINE : ('Receita' as SEARCH_TYPE),
        icon: MedicineIcon,
        searchable: true,
        visible: canShowPrescription,
        itemCount: medicineCount,
      },
      {
        type: SEARCH_TYPE.FORMULA,
        label: canShowFormula ? SEARCH_TYPE.FORMULA : ('Receita' as SEARCH_TYPE),
        icon: FormulaIcon,
        searchable: true,
        visible: canShowFormula,
        itemCount: formulaCount,
      },
      {
        type: SEARCH_TYPE.EXAMS,
        label: prescriberConfigurations?.Features?.ExamsLabel ? SEARCH_TYPE.EXAMS_ONLY : SEARCH_TYPE.EXAMS,
        icon: ExamIcon,
        searchable: true,
        visible: canAddExam,
        itemCount: examCount,
      },
      {
        type: SEARCH_TYPE.MEDICAL_CERTIFICATE,
        label: SEARCH_TYPE.MEDICAL_CERTIFICATE,
        icon: MedicalCertificateIcon,
        searchable: false,
        visible: canAddMedicalCertificate,
        itemCount: certificateCount,
      },
      {
        type: SEARCH_TYPE.MEDICAL_REFERRAL,
        label: SEARCH_TYPE.MEDICAL_REFERRAL,
        icon: MedicalReferralIcon,
        searchable: false,
        visible: canAddMedicalReferral,
        itemCount: referralCount,
      },
      {
        type: SEARCH_TYPE.MEDICAL_REPORT,
        label: SEARCH_TYPE.MEDICAL_REPORT,
        icon: MedicalReportIcon,
        searchable: false,
        visible: canAddMedicalReport,
        itemCount: reportCount,
      },
    ];
  }, [
    canAddExam,
    canAddMedicalCertificate,
    canAddMedicalReferral,
    canAddMedicalReport,
    canShowMedicalPrescription,
    canShowPrescription,
    canShowFormula,
    medicineCount,
    formulaCount,
    examCount,
    certificateCount,
    referralCount,
    reportCount,
    prescriberConfigurations?.Features?.ExamsLabel,
  ]);

  const visibleItems = useMemo(() => {
    return searchTypeContent.filter((item) => item.visible);
  }, [searchTypeContent]);

  // Show/hide blur based on scroll position
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkScroll = () => {
      setShowBlurLeft(el.scrollLeft > 2);
      setShowBlurRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    };

    checkScroll();
    el.addEventListener('scroll', checkScroll);

    const resizeObs = new ResizeObserver(checkScroll);
    resizeObs.observe(el);

    return () => {
      el.removeEventListener('scroll', checkScroll);
      resizeObs.disconnect();
    };
  }, [visibleItems]);

  const handleChange = useCallback(
    (item: ItemContent) => {
      if (item.type === selectedSearchType) return;
      onChange(item);
    },
    [onChange, selectedSearchType]
  );

  if (prescriptionLoading) {
    return (
      <Box mb={'4px'} display={'flex'} gap={'16px'}>
        <Skeleton height={'36px'} width={'120px'} variant={'rect'} borderRadius={'4px'} />
        <Skeleton height={'36px'} width={'80px'} variant={'rect'} borderRadius={'4px'} />
        <Skeleton height={'36px'} width={'140px'} variant={'rect'} borderRadius={'4px'} />
      </Box>
    );
  }

  return (
    <div
      style={{ borderBottom: '1px solid #e9ecee', position: 'relative' }}
      data-tut="search-type-select"
    >
      <HorizontalScrollContainer ref={scrollRef}>
        {visibleItems.map((item) => (
          <OptionButton
            key={item.type}
            buttonData={item as ItemContent}
            onClick={() => handleChange(item as ItemContent)}
            isSelected={selectedSearchType === item.type}
            primaryColor={props.primaryColor}
          />
        ))}
      </HorizontalScrollContainer>
      {showBlurLeft && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: '56px',
            background: 'linear-gradient(to right, #f8f9fa 0%, rgba(248,249,250,0) 100%)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}
      {showBlurRight && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            height: '100%',
            width: '56px',
            background: 'linear-gradient(to left, #f8f9fa 0%, rgba(248,249,250,0) 100%)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}
    </div>
  );
}

export default React.memo(SearchTypes);
